<script setup>
import { computed, ref, onMounted, onUnmounted } from 'vue';
import { Head, Link, router } from '@inertiajs/vue3';
import FrontLayout from '@/Layouts/FrontLayout.vue';

const props = defineProps({
    setting: Object,
    news: Object,
    latestNews: Array,
    categories: Array,
    popularTags: Array,
    archives: Array,
    category: Object,
    tag: Object,
    filterYear: [String, Number],
    filterMonth: [String, Number],
    title: String,
    meta_description: String,
    meta_keywords: String,
    canonical_url: String, // from controller
    og_title: String,
    og_description: String,
    og_image: String, // from controller
    og_url: String, // from controller
    twitter_title: String,
    twitter_description: String,
    twitter_image: String, // from controller
});

// WA Logic
const wa = computed(() => props.setting?.setting_no_wa || '6281234567890');
const waUrl = computed(() => `https://wa.me/${wa.value}?text=Halo,%20saya%20ingin%20info%20lebih%20lanjut`);
const fmt = (v) => new Intl.NumberFormat('id-ID').format(v || 0);

// Helper for SEO
const pageTitle = computed(() => props.title || 'Berita & Artikel - ' + (props.setting?.setting_title || 'Gotransport'));
const pageDesc = computed(() => props.meta_description || 'Update terbaru seputar dunia transportasi, wisata, dan rental mobil.');

// Mobile Menu
const isMobileMenuOpen = ref(false);
const toggleMobileMenu = () => {
    isMobileMenuOpen.value = !isMobileMenuOpen.value;
};

// Search
const searchQuery = ref(new URLSearchParams(window.location.search).get('search') || '');
const handleSearch = () => {
    router.visit('/berita-dan-artikel', {
        method: 'get',
        data: { search: searchQuery.value },
        preserveState: true,
        preserveScroll: true,
        only: ['news'],
    });
};

// Helper Dates
const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('id-ID', { day: 'numeric', month: 'long', year: 'numeric' }).format(date);
};
const getMonthName = (monthNumber) => {
    const date = new Date();
    date.setMonth(monthNumber - 1);
    return new Intl.DateTimeFormat('id-ID', { month: 'long' }).format(date);
};

// Breadcrumbs
// Armada moves Breadcrumb to TOP of Hero.
const heroTitle = computed(() => {
    if (props.category) return props.category.name;
    if (props.tag) return 'Tag: ' + props.tag.name;
    if (props.filterYear && props.filterMonth) return `Arsip ${getMonthName(props.filterMonth)} ${props.filterYear}`;
    if (searchQuery.value) return `Cari: "${searchQuery.value}"`;
    return 'Berita & Artikel';
});

const heroSubtitle = computed(() => {
    if (props.category) return `Artikel dalam kategori ${props.category.name}`;
    if (props.tag) return `Artikel dengan tag ${props.tag.name}`;
    return 'Informasi terbaru seputar transportasi dan wisata';
});

const getImageUrl = (image) => image ? `/storage/news/${image}` : '/front/img/default-news.jpg';

// Helper for Pagination Label
const makeLabel = (label) => {
    if (label.includes('Previous') || label.includes('&laquo;')) return '<i class="fa fa-angle-left"></i>';
    if (label.includes('Next') || label.includes('&raquo;')) return '<i class="fa fa-angle-right"></i>';
    return label;
};

</script>

<template>
    <FrontLayout 
        :setting="setting" 
        :title="pageTitle" 
        :metaDescription="pageDesc"
        :og_title="props.og_title || pageTitle"
        :og_description="props.og_description || pageDesc"
        :og_image="props.og_image"
        :og_url="props.og_url"
        :twitter_title="props.twitter_title || pageTitle"
        :twitter_description="props.twitter_description || pageDesc"
        :twitter_image="props.twitter_image"
        :meta_keywords="meta_keywords"
    >
        <Head>
            <link v-if="canonical_url" rel="canonical" :href="canonical_url" />
            <meta property="og:type" content="article" />
            <meta property="og:site_name" content="Gotransport Banyuwangi" />
            <meta name="twitter:card" content="summary_large_image" />
        </Head>

        <!-- Hero Section (Exact Copy from Armada with Orange Gradient) -->
        <section class="relative overflow-hidden">
             <div class="absolute inset-0 bg-gradient-to-br from-orange-600 via-orange-500 to-amber-400"></div>
             <div class="absolute inset-0 overflow-hidden pointer-events-none">
                <svg class="absolute right-0 top-0 text-white/10 w-64 h-64 -mr-16 -mt-16" fill="currentColor" viewBox="0 0 100 100"><circle cx="50" cy="50" r="50"/></svg>
                <div class="absolute left-10 bottom-10 w-20 h-20 bg-white/10 rounded-full blur-xl"></div>
             </div>

             <div class="container mx-auto px-4 lg:px-6 xl:px-8 relative z-10 text-center py-12 md:py-16">
                 <div class="flex justify-center mb-6">
                     <div class="inline-flex items-center gap-2 text-xs md:text-sm text-white/90 bg-white/10 backdrop-blur-md px-4 py-1.5 rounded-full border border-white/20 shadow-sm">
                        <Link href="/" class="hover:text-white transition-colors font-medium">Beranda</Link>
                        <i class="fa fa-chevron-right text-[10px] opacity-70"></i>
                        <span class="text-white font-bold">{{ heroTitle }}</span>
                     </div>
                 </div>

                 <h1 class="text-3xl md:text-4xl lg:text-5xl font-extrabold text-white mb-4 tracking-tight drop-shadow-sm">{{ heroTitle }}</h1>
                 <p class="text-amber-100 text-lg max-w-2xl mx-auto font-medium">{{ heroSubtitle }}</p>
             </div>
             
             <div class="absolute bottom-0 left-0 right-0 text-gray-50 dark:text-gray-900 transition-colors duration-300">
                 <svg viewBox="0 0 1440 48" fill="currentColor" class="w-full h-auto block">
                     <path d="M0,48 L1440,48 L1440,0 C1440,0 1140,48 720,48 C300,48 0,0 0,0 L0,48 Z"></path>
                 </svg>
             </div>
        </section>

        <!-- Content Section -->
        <section class="py-8 md:py-12 bg-gray-50 dark:bg-gray-900 min-h-[600px] transition-colors duration-300">
            <div class="container mx-auto px-4 lg:px-6 xl:px-8 max-w-7xl">
                <div class="grid grid-cols-1 lg:grid-cols-3 gap-8">
                    
                    <!-- Main Content -->
                    <div class="lg:col-span-2">
                         <div v-if="news.data && news.data.length > 0" class="space-y-6">
                            <article v-for="article in news.data" :key="article.id" class="group bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-2xl overflow-hidden hover:border-orange-400 hover:shadow-xl transition-all duration-300 flex flex-col md:flex-row items-stretch">
                                <!-- Image -->
                                <div class="md:w-1/3 relative overflow-hidden shrink-0">
                                    <div class="hidden md:block absolute inset-0">
                                         <Link :href="`/artikel/${article.slug}`" class="block h-full w-full">
                                            <div class="absolute inset-0 bg-gray-200 animate-pulse" v-if="!article.image"></div>
                                            <img :src="getImageUrl(article.image)" :alt="article.title" class="w-full h-full object-cover" loading="lazy" />
                                         </Link>
                                    </div>
                                    <!-- Mobile Aspect Ratio Wrapper -->
                                    <div class="block md:hidden relative aspect-[4/3]">
                                         <Link :href="`/artikel/${article.slug}`" class="absolute inset-0 h-full w-full">
                                            <div class="absolute inset-0 bg-gray-200 animate-pulse" v-if="!article.image"></div>
                                            <img :src="getImageUrl(article.image)" :alt="article.title" class="w-full h-full object-cover" loading="lazy" />
                                         </Link>
                                    </div>
                                </div>

                                <!-- Info -->
                                <div class="p-4 flex-1 flex flex-col justify-between">
                                    <div>
                                        <div class="flex items-center gap-3 text-xs text-gray-500 mb-3 font-medium flex-wrap">
                                            <span class="flex items-center gap-1"><i class="fa fa-calendar text-orange-500"></i> {{ formatDate(article.published_at) }}</span>
                                            <span class="flex items-center gap-1"><i class="fa fa-user text-orange-500"></i> {{ article.author || 'Admin' }}</span>
                                            <span class="flex items-center gap-1 text-blue-600 font-bold" v-if="article.category_model">
                                                <i class="fa fa-tag"></i> {{ article.category_model.name }}
                                            </span>
                                        </div>
                                        <h2 class="text-xl font-bold text-gray-900 dark:text-white mb-2 leading-snug group-hover:text-amber-600 transition-colors">
                                            <Link :href="`/artikel/${article.slug}`">{{ article.title }}</Link>
                                        </h2>

                                        <p class="text-gray-600 dark:text-gray-300 text-sm line-clamp-3 mb-4 leading-relaxed">
                                            {{ article.excerpt }}
                                        </p>
                                    </div>
                                    
                                    <div class="flex items-center justify-between pt-4 border-t border-gray-100 dark:border-gray-700">
                                        <Link :href="`/artikel/${article.slug}`" class="text-amber-600 font-semibold text-sm hover:text-amber-700 flex items-center gap-1 group/btn">
                                            Baca Selengkapnya <i class="fa fa-arrow-right group-hover/btn:translate-x-1 transition-transform"></i>
                                        </Link>
                                        <div class="flex items-center gap-3 text-gray-400 text-xs">
                                            <span class="flex items-center gap-1"><i class="fa fa-eye"></i> {{ article.views || 0 }}</span>
                                        </div>
                                    </div>
                                </div>
                            </article>
                         </div>

                         <!-- Empty State -->
                         <div v-else class="text-center py-20 bg-white dark:bg-gray-800 rounded-2xl border border-dashed border-gray-300 dark:border-gray-700">
                             <div class="w-20 h-20 bg-gray-50 rounded-full flex items-center justify-center mx-auto mb-4">
                                 <i class="fa fa-search text-gray-300 text-3xl"></i>
                             </div>
                             <h3 class="text-xl font-bold text-gray-900 dark:text-white mb-2">Tidak ada artikel ditemukan</h3>
                             <p class="text-gray-500 mb-6 max-w-md mx-auto">Coba gunakan kata kunci lain atau telusuri kategori yang tersedia.</p>
                             <Link href="/berita-dan-artikel" class="px-6 py-2.5 bg-orange-500 hover:bg-orange-600 text-white rounded-full font-bold transition-colors shadow-lg shadow-orange-500/30">
                                 Reset Filter
                             </Link>
                         </div>

                            <!-- Pagination -->
                        <div class="mt-12 flex justify-center" v-if="news.links && news.links.length > 3">
                            <div class="flex flex-wrap gap-2 justify-center">
                                <template v-for="(link, key) in news.links" :key="key">
                                    <div v-if="link.url === null" class="px-3 py-2 text-sm text-gray-400 border border-gray-200 dark:border-gray-700 rounded-lg bg-gray-50 dark:bg-gray-800 flex items-center justify-center min-w-[40px]" v-html="makeLabel(link.label)" />
                                    <Link v-else class="px-3 py-2 text-sm border rounded-lg transition-all duration-200 flex items-center justify-center min-w-[40px]" 
                                          :class="link.active ? 'bg-orange-500 text-white border-orange-500 shadow-md' : 'bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 border-gray-200 dark:border-gray-700 hover:bg-orange-50 dark:hover:bg-gray-700 hover:text-orange-600 hover:border-orange-300'"
                                          :href="link.url" v-html="makeLabel(link.label)" />
                                </template>
                            </div>
                        </div>
                    </div>


                    <!-- Sidebar -->
                    <aside class="lg:col-span-1 space-y-6">
                        <!-- Search Widget -->
                        <div class="bg-white dark:bg-gray-800 p-4 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 transition-colors">
                             <h3 class="text-lg font-bold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                                <i class="fa fa-search text-orange-500"></i> Cari Artikel
                             </h3>
                             <form @submit.prevent="handleSearch" class="relative">
                                 <input v-model="searchQuery" type="text" placeholder="Kata kunci..." class="w-full px-4 py-2.5 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-100 focus:border-orange-400 transition-all text-sm text-gray-900 dark:text-white dark:placeholder-gray-400">
                                 <button type="submit" class="absolute right-2 top-2 p-1.5 text-gray-400 hover:text-orange-600 transition-colors">
                                     <i class="fa fa-search"></i>
                                 </button>
                             </form>
                        </div>
                        
                        <!-- Latest News Widget -->
                         <div class="bg-white dark:bg-gray-800 p-4 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 transition-colors" v-if="latestNews && latestNews.length">
                             <h3 class="text-lg font-bold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                                <i class="fa fa-bolt text-orange-500"></i> Terbaru
                             </h3>
                             <div class="space-y-4">
                                 <div v-for="item in latestNews" :key="item.id" class="flex gap-3 group">
                                     <div class="w-16 h-16 rounded-lg overflow-hidden flex-shrink-0">
                                         <Link :href="`/artikel/${item.slug}`">
                                             <img :src="getImageUrl(item.image)" class="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" :alt="item.title">
                                         </Link>
                                     </div>
                                     <div class="flex-1">
                                         <h4 class="text-sm font-bold text-gray-900 dark:text-gray-100 leading-snug group-hover:text-orange-600 transition-colors line-clamp-2 mb-1">
                                             <Link :href="`/artikel/${item.slug}`">{{ item.title }}</Link>
                                         </h4>
                                         <span class="text-xs text-gray-400"><i class="fa fa-clock-o mr-1"></i> {{ formatDate(item.published_at) }}</span>
                                     </div>
                                 </div>
                             </div>
                        </div>
                    </aside>
                </div>

                <!-- Bottom Widgets Section -->
                <div class="grid grid-cols-1 md:grid-cols-3 gap-6 mt-12">
                     <!-- Categories Widget -->
                     <div class="bg-white dark:bg-gray-800 p-4 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 col-span-1 transition-colors" v-if="categories && categories.length">
                          <h3 class="text-lg font-bold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                             <i class="fa fa-folder text-orange-500"></i> Kategori
                          </h3>
                          <div class="flex flex-col">
                              <Link v-for="(cat, idx) in categories" :key="cat.id" :href="`/berita-dan-artikel/kategori/${cat.slug}`" 
                                    class="flex items-center justify-between py-3 border-b border-gray-100 last:border-0 hover:pl-2 transition-all duration-300 group">
                                  <div class="flex items-center gap-3">
                                      <i class="fa fa-folder text-gray-400 group-hover:text-orange-500 transition-colors"></i>
                                      <span class="text-gray-600 dark:text-gray-300 font-medium text-sm group-hover:text-orange-600 transition-colors">{{ cat.name }}</span>
                                  </div>
                                  <span class="bg-gray-100 text-gray-500 text-xs py-1 px-2 rounded group-hover:bg-orange-100 group-hover:text-orange-600 transition-colors">{{ cat.news_count }}</span>
                              </Link>
                          </div>
                     </div>

                     <!-- Archive Widget -->
                     <div class="bg-white dark:bg-gray-800 p-4 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 col-span-1 transition-colors" v-if="archives && archives.length">
                          <h3 class="text-lg font-bold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                             <i class="fa fa-archive text-orange-500"></i> Arsip
                          </h3>
                          <div class="flex flex-col">
                              <Link v-for="(archive, idx) in archives" :key="idx" :href="`/berita-dan-artikel/arsip/${archive.year}/${archive.month}`"
                                    class="flex items-center justify-between py-3 border-b border-gray-100 last:border-0 hover:pl-2 transition-all duration-300 group">
                                  <div class="flex items-center gap-3">
                                      <i class="fa fa-calendar text-gray-400 group-hover:text-orange-500 transition-colors"></i>
                                      <span class="text-gray-600 dark:text-gray-300 font-medium text-sm group-hover:text-orange-600 transition-colors">{{ getMonthName(archive.month) }} {{ archive.year }}</span>
                                  </div>
                                  <span class="bg-gray-100 text-gray-500 text-xs py-1 px-2 rounded group-hover:bg-orange-100 group-hover:text-orange-600 transition-colors">{{ archive.count }}</span>
                              </Link>
                          </div>
                     </div>

                     <!-- Tags Widget -->
                     <div class="bg-white dark:bg-gray-800 p-4 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 col-span-1 transition-colors" v-if="popularTags && popularTags.length">
                          <h3 class="text-lg font-bold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                             <i class="fa fa-tags text-orange-500"></i> Tag Populer
                          </h3>
                          <div class="flex flex-wrap gap-2">
                              <Link v-for="t in popularTags" :key="t.id" :href="`/berita-dan-artikel/tag/${t.slug}`" 
                                    class="px-3 py-1 text-xs font-bold rounded-full border transition-all"
                                    :class="t.color ? 'text-black hover:opacity-80 shadow-sm' : 'bg-gray-50 hover:bg-orange-500 hover:text-white text-gray-600 border-gray-200 hover:border-orange-500'"
                                    :style="t.color ? { backgroundColor: t.color, borderColor: t.color } : {}">
                                  #{{ t.name }}
                              </Link>
                          </div>
                     </div>
                </div>
            </div>
        </section>
    </FrontLayout>
</template>
